// @ts-ignore
import { Payment } from 'gamelib-sdk-web/lib/webpay2/payment'
import { api } from '@/api/appGatewayServerApi'
import { getLanguage } from '@/i18n'
import { sendLog, sendLogParams } from '@/utils/log'
import { isProduction } from '@/utils/env'
import { sendApiWarning, showGenericNetworkError } from '@/utils/handle_error'
import { LANG_JA, LANG_EN } from '../i18n/index'
import * as models from '@/models'

const labApiKey =
  'pk_test_51IOGCRAkcTVbbArdrOZaEQbYmuPXHWRTYhy9YrzB3OenlYGltyk5Dg78G4Ly4Wffcvab3FeMBhzVqp4KzCbnsy9o001Yhicj03'
const prodApiKey =
  'pk_live_51IOGCRAkcTVbbArdb3R236Ysrw3kTmGv2lki11e1Xw7zBX4Np5IJPpW25vdQasa47CO439nyWX8i3E9CTNNIDtqX00NZphNbcl'

const apiKey = isProduction() ? prodApiKey : labApiKey

// エラーコード
export const ErrorCodePurchaseCannotCancelFinishedPayment = 285540652 // 決済済み支払いのキャンセルエラー

export function createHandler(cb: {
  onReady?: CallableFunction
  onCompleted?: CallableFunction
  onFailure?: CallableFunction
  onFormLoaded?: CallableFunction
  onSetupOrder?: CallableFunction
  onPurchaseFinished?: CallableFunction
  onUnauthorizedSubmit?: CallableFunction
}): { handler: gamelib.webpay.IHandler; authorizeNextPurchase: () => void } {
  // purchase() → submitButton.click() の正規ルートを通ったことを示すフラグ。
  // クロージャ変数で管理することで this バインディングの問題を排除する。
  let purchaseAuthorized = false

  const handler: gamelib.webpay.IHandler = {
    readyForPurchase: async (purchaseId, info) => {
      // purchase() を経由せずに submit された場合（Enterキー等による非正規ルート）は
      // payment.record も含めて全処理を中断する。
      if (!purchaseAuthorized) {
        if (cb.onUnauthorizedSubmit) cb.onUnauthorizedSubmit()
        throw new Error(`readyForPurchase error: purchase was not authorized for purchaseId ${purchaseId}`)
      }
      // フラグは1回限り有効（次のsubmitに持ち越さない）
      purchaseAuthorized = false

      await api().web.payment.record({
        eventName: 'readyForPurchase',
        purchaseId,
        params: JSON.stringify({ info }),
      })

      if (cb.onReady && !(await cb.onReady())) {
        throw new Error(`readyForPurchase error: Callback onReady did not return a truthy value for purchaseId ${purchaseId}`)
      }

      // 暫定対応：ready成功時に purchase_id をローカルにセットする
      setPurchase(purchaseId)

      return true
    },
    orderComplete: async (purchaseId, info, receipt) => {
      // INFO:後払い決済（コンビニ決済）支払いの場合、orderCompleteの代わりにpurchaseFinishedが呼ばれる
      const params = JSON.stringify({ info, receipt })
      await api()
        .streamer.currency.coin.purchase.commit({
          purchaseId: purchaseId,
          providerId: models.ProviderIdTypes.Stripe,
          params: params,
        })
        .catch((e) => {
          sendApiWarning(e)
          showGenericNetworkError()
        })

      // 正常に purchase/commit を叩けたら消す
      clearPurchase()

      if (cb.onCompleted) cb.onCompleted()
      return true
    },
    orderFailure: async (purchaseId, info, receipt, error) => {
      const params = JSON.stringify({ info, receipt, error })
      await api()
        .streamer.currency.coin.purchase.commit({
          purchaseId: purchaseId,
          providerId: models.ProviderIdTypes.Stripe,
          params: params,
        })
        .catch(() => {
          // 確実にエラーで返るので、例外は握りつぶす
        })

      // 正常に purchase/commit を叩けたら消す
      clearPurchase()

      if (cb.onFailure) cb.onFailure(error)
      return true
    },
    formLoaded: async (_productId, _purchaseId) => {
      // INFO:タイミングはreadyForPurchase処理前
      // （もし支払いを開始いたくないケースが出てきた場合はここにエラーを投げる処理を書く）

      if (cb.onFormLoaded) cb.onFormLoaded()

      return true
    },
    setupOrder: async (purchaseId, info) => {
      const params = JSON.stringify({ info })
      const paymentMethodType = info.payment_method_type
      const setupFutureUsage = info.setup_future_usage
      await api()
        .streamer.currency.coin.purchase.update({
          purchaseId: purchaseId,
          providerId: models.ProviderIdTypes.Stripe,
          paymentMethodType: paymentMethodType,
          setupFutureUsage: setupFutureUsage,
          params: params,
        })
        .catch((e) => {
          sendApiWarning(e)
          showGenericNetworkError()
        })

      // コンビニ決済の場合はこの時点でclearPurchaseしておく
      if (info.payment_method_type === 'konbini') {
        clearPurchase()
      }

      if (cb.onSetupOrder) cb.onSetupOrder(purchaseId, info)

      return true
    },
    purchaseFinished: async (purchaseId, info, receipt) => {
      // INFO:後払い決済（コンビニ決済）支払いが正常に完了したときに呼び出される
      if (cb.onPurchaseFinished)
        cb.onPurchaseFinished(purchaseId, info, receipt)

      return true
    },
  }

  return {
    handler,
    authorizeNextPurchase: () => {
      purchaseAuthorized = true
    },
  }
}

const logger: gamelib.logging.ILogger = {
  debug: async (code, desc) => {
    // glib:d002 は決済時エラーのデバッグ情報なので拾う
    if (code === 'glib:d002') {
      await sendLogParams('gamelib debug d002.', { code, desc })
    }
  },
  info: async (pos, envs) => {
    await sendLogParams('gamelib info.', { pos, envs })
  },
  error: async (code, message, envs) => {
    const purchaseId = envs.purchase_id || 'null'

    // エラーのログは永続化が必要そうなので BigQuery 側にも落とす
    // orderFailure 時にも落としているが、orderFailure が呼ばれないエラーパターンもありそう
    await Promise.all([
      api().web.payment.record({
        eventName: code,
        purchaseId,
        params: JSON.stringify({ code, message, envs }),
      }),
      sendLogParams('gamelib error.', { code, message, envs }),
    ])
  },
}

export async function createPayment(
  formPromise: Promise<gamelib.webpay.model.IPurchaseForm>,
  handler: gamelib.webpay.IHandler
): Promise<gamelib.webpay.Payment> {
  const payment: gamelib.webpay.Payment = new Payment(apiKey)

  payment.acceptHandler(handler)
  payment.setLogger(logger)

  // Paymentオブジェクトのload完了を待つ必要がある
  await payment.initialize(formPromise, {
    locale: getLanguage() === LANG_JA ? LANG_JA : LANG_EN,
    rewind_form_after_payerror: true,
  })

  return payment
}

const purchaseIdKey = 'purchase_id'

function getPurchase() {
  return localStorage.getItem(purchaseIdKey)
}

function setPurchase(purchaseId: string) {
  localStorage.setItem(purchaseIdKey, purchaseId)
}

function clearPurchase() {
  localStorage.removeItem(purchaseIdKey)
}

export async function checkPurchase() {
  const purchaseId = getPurchase()
  if (!purchaseId) {
    return
  }

  // purchaseId が残っていた場合は決済不整合が発生している可能性があるため、
  // その purchaseId に対して purchase/commit API を叩く
  try {
    await api().streamer.currency.coin.purchase.commit({
      purchaseId,
      providerId: models.ProviderIdTypes.Stripe,
      params: JSON.stringify({
        event: 'restore_purchase',
      }),
    })
  } catch (e) {
    sendApiWarning(e)
  }
  clearPurchase()
  sendLog(`purchase commit in mypage. purchaseId:${purchaseId}`, 'warning')
}
